import { useState } from 'react'
import ResizableModal from '../../ui/ResizableModal'
import { Button, Select, Title } from '../../ui/index'
import { sileo } from 'sileo'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { useQueryGetPayments } from '../../../../queries/paymentQueries'

const TYPE_LABELS = {
  platform_error: 'Error de plataforma',
  payment_refund: 'Reembolso de pago'
}

export const CreateTicketModal = ({ open, onClose, onCreateTicket, isCreating }) => {
  const [form, setForm] = useState({
    title: '',
    type: 'platform_error',
    payment_id: '',
    message: ''
  })
  const [files, setFiles] = useState([])
  const { data: payments = [], isLoading: isLoadingPayments } = useQueryGetPayments()

  const handleFileChange = e => {
    const newFiles = Array.from(e.target.files)
    if (newFiles.length + files.length > 10) {
      sileo.error({
        title: 'Límite de Archivos',
        description: (
          <span className='text-red-500/80 font-medium'>Máximo 10 archivos permitidos</span>
        )
      })
      return
    }
    setFiles(prev => [...prev, ...newFiles])
  }

  const removeFile = index => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      sileo.error({
        title: 'Campo Requerido',
        description: <span className='text-red-500/80 font-medium'>El título es obligatorio</span>
      })
      return
    }

    if (form.type === 'payment_refund' && !form.payment_id) {
      sileo.error({
        title: 'ID Requerido',
        description: (
          <span className='text-red-500/80 font-medium'>
            El ID de pago es obligatorio para reembolsos
          </span>
        )
      })
      return
    }

    if (files.length > 10) {
      sileo.error({ title: 'Máximo 10 archivos permitidos' })
      return
    }

    try {
      await onCreateTicket({
        title: form.title,
        type: form.type,
        metadata:
          form.type === 'payment_refund' ? { payment_id: Number(form.payment_id) } : undefined,
        message: form.message.trim() || undefined,
        files: files.length > 0 ? files : undefined
      })
      setForm({ title: '', type: 'platform_error', payment_id: '', message: '' })
      setFiles([])
      onClose()
    } catch (error) {
      console.error('Error al crear ticket:', error)
    }
  }

  const handleClose = () => {
    setForm({ title: '', type: 'platform_error', payment_id: '', message: '' })
    setFiles([])
    onClose()
  }

  const formatAmount = amount => {
    if (!amount) return '$0'
    const numAmount = parseFloat(amount)
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(numAmount)
  }

  const formatDate = dateString => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getPaymentOptions = () => {
    return payments.map(payment => ({
      value: payment.id.toString(),
      label: `ID: ${payment.id} - ${payment.description || 'Sin descripción'} - ${formatAmount(
        payment.amount
      )} - ${formatDate(payment.createdAt)}`
    }))
  }

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      width={800}
      height={700}
      minWidth={600}
      minHeight={500}
      footer={
        <>
          <Button
            onClick={handleClose}
            className='uppercase text-sm bg-primary text-white rounded-full'
            title='CANCELAR'
          />
          <Button
            onClick={handleSubmit}
            disabled={isCreating}
            className='uppercase text-sm bg-accent2 text-white rounded-full'
            title='CREAR'
          />
        </>
      }
    >
      <div className='relative w-full mx-auto'>
        <div className='flex items-center justify-between mb-4'>
          <Title className='text-2xl'>Crear Nuevo Ticket</Title>
        </div>

        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium mb-2'>Título *</label>
            <input
              type='text'
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder='Ej: Error al procesar pago'
              className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent2'
            />
          </div>
          <div>
            <label className='block text-sm font-medium mb-2'>Tipo *</label>
            <Select
              defaultValue={form.type}
              onChange={e => setForm({ ...form, type: e.target.value })}
              options={[
                { value: 'platform_error', label: TYPE_LABELS.platform_error },
                { value: 'payment_refund', label: TYPE_LABELS.payment_refund }
              ]}
            />
          </div>
          {form.type === 'payment_refund' && (
            <div>
              <label className='block text-sm font-medium mb-2'>Pago a Reembolsar *</label>
              {isLoadingPayments ? (
                <div className='flex items-center justify-center py-4'>
                  <div className='animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-accent2'></div>
                  <span className='ml-2 text-sm text-gray-600'>Cargando pagos...</span>
                </div>
              ) : payments.length === 0 ? (
                <p className='text-sm text-gray-500 py-2'>No hay pagos disponibles</p>
              ) : (
                <Select
                  defaultValue={form.payment_id}
                  onChange={e => setForm({ ...form, payment_id: e.target.value })}
                  options={[{ value: '', label: 'Seleccione un pago' }, ...getPaymentOptions()]}
                />
              )}
            </div>
          )}
          <div>
            <label className='block text-sm font-medium mb-2'>Mensaje inicial (opcional)</label>
            <textarea
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              placeholder='Describe tu problema o solicitud...'
              className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent2'
              rows={4}
            />
            <p className='text-xs text-gray-500 mt-1'>
              Puedes agregar un comentario inicial y archivos adjuntos al crear el ticket
            </p>
          </div>
          <div>
            <input
              type='file'
              id='file-upload-create'
              multiple
              onChange={handleFileChange}
              className='hidden'
            />
            <label
              htmlFor='file-upload-create'
              className='inline-flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer bg-white hover:opacity-80'
            >
              <AttachFileIcon fontSize='small' />
              <span className='text-sm'>Adjuntar archivos (máx 10)</span>
            </label>
            {files.length > 0 && (
              <div className='mt-2 space-y-1'>
                {files.map((file, index) => (
                  <div
                    key={index}
                    className='flex items-center justify-between text-sm bg-gray-50 p-2 rounded'
                  >
                    <span>{file.name}</span>
                    <IconButton size='small' onClick={() => removeFile(index)}>
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ResizableModal>
  )
}
